import {
  EMPTY_INPUT,
  type InputDeviceMode,
  type InputState,
} from "@neon-arena/shared";
import { GamepadInput } from "./GamepadInput.js";
import { TouchInput, isTouchDevice } from "./TouchInput.js";
import { composeInputState } from "./composeInputState.js";
import { getBindings, type Bindings } from "./Bindings.js";
import type { DroneInput } from "../game/DroneCameraController.js";

export type { DroneInput };

const DRONE_LOOK_SENSITIVITY = 0.002;
const DRONE_CONTROLLER_LOOK_SPEED = 2.5;  // rad/s
const DRONE_DEADZONE = 0.15;

type Listener = (mode: InputDeviceMode) => void;

/** Camera control input — always read separately from game InputState. */
export interface CameraInput {
  /** Keyboard: hold C to look behind the car (third-person only). */
  lookBehind: boolean;
  /** Controller right stick X — orbit camera left/right. Controller only. */
  freeLookX: number;
  /** Controller right stick Y — tilt camera up/down. Controller only. */
  freeLookY: number;
}

export class InputManager {
  private keys = new Set<string>();
  private mouseFire = false;
  private mouseRightHeld = false;
  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private gamepad: GamepadInput;
  private touch: TouchInput;
  private mode: InputDeviceMode = "keyboard";
  private modeListeners: Listener[] = [];
  private menuListeners: Array<() => void> = [];
  private spectateNextListeners: Array<() => void> = [];
  private cameraModeListeners: Array<() => void> = [];
  private mirrorCycleListeners: Array<() => void> = [];
  private gamepadConnected = false;
  private connectionListeners: Array<(connected: boolean, name: string) => void> = [];
  private prevDroneButtons: boolean[] = [];
  private keyboardOnly = false;
  // When true, never fall back to keyboard — this window owns a specific gamepad index.
  private gamepadOnly = false;
  private gamepadIndex = 0;

  constructor(gamepadIndex = 0) {
    this.gamepadIndex = gamepadIndex;
    this.gamepad = new GamepadInput(gamepadIndex);
    this.gamepadOnly = gamepadIndex > 0;
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("contextmenu", (e) => e.preventDefault());
    window.addEventListener("blur", () => this.keys.clear());
    window.addEventListener("gamepadconnected", this.onGamepadConnected);
    window.addEventListener("gamepaddisconnected", this.onGamepadDisconnected);

    this.touch = new TouchInput();
    this.touch.onCameraToggle(() => this.fireCameraToggle());
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("gamepadconnected", this.onGamepadConnected);
    window.removeEventListener("gamepaddisconnected", this.onGamepadDisconnected);
    this.touch.destroy();
  }

  onModeChange(fn: Listener): void { this.modeListeners.push(fn); }
  onMenuPress(fn: () => void): void { this.menuListeners.push(fn); }
  onSpectateNext(fn: () => void): void { this.spectateNextListeners.push(fn); }
  onCameraToggle(fn: () => void): void { this.cameraModeListeners.push(fn); }
  onMirrorCycle(fn: () => void): void { this.mirrorCycleListeners.push(fn); }

  fireCameraToggle(): void {
    for (const fn of this.cameraModeListeners) fn();
  }

  fireMirrorCycle(): void {
    for (const fn of this.mirrorCycleListeners) fn();
  }

  getTouchInput(): TouchInput { return this.touch; }

  onGamepadConnectionChange(fn: (connected: boolean, name: string) => void): void {
    this.connectionListeners.push(fn);
  }

  setControlMethod(method: "keyboard" | "controller" | "touch"): void {
    if (method === "keyboard") {
      this.keyboardOnly = false; // auto-detect: keyboard + gamepad both work
      this.gamepadOnly = false;
      this.mode = "keyboard";
      this.touch.hide();
    } else if (method === "controller") {
      this.keyboardOnly = false;
      this.gamepadOnly = true;
      this.mode = "gamepad";
      this.touch.hide();
    } else {
      this.keyboardOnly = false;
      this.mode = "touch";
      this.touch.show();
      this.touch.bindHideButton();
    }
  }

  lockToKeyboard(): void {
    this.keyboardOnly = true;
    this.mode = "keyboard";
    this.touch.hide();
  }

  getMode(): InputDeviceMode { return this.mode; }

  /** Read drone observer controls. Tab cycling is handled via onSpectateNext callback. */
  readDrone(dt: number): DroneInput {
    const k = this.keys;

    // Keyboard
    let moveZ = (k.has("KeyW") ? 1 : 0) - (k.has("KeyS") ? 1 : 0);
    let moveX = (k.has("KeyD") ? 1 : 0) - (k.has("KeyA") ? 1 : 0);
    let moveY = (k.has("KeyE") ? 1 : 0) - (k.has("KeyQ") ? 1 : 0);
    let fast  = k.has("ShiftLeft") || k.has("ShiftRight");
    let followToggle = k.has("KeyF");
    let releaseFollow = false;
    let nextTarget = false;
    let previousTarget = false;

    const useLook = this.mouseRightHeld || document.pointerLockElement !== null;
    let lookX = useLook ? this.mouseDeltaX * DRONE_LOOK_SENSITIVITY : 0;
    let lookY = useLook ? this.mouseDeltaY * DRONE_LOOK_SENSITIVITY : 0;
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;

    // Gamepad overlay — find the nth connected gamepad matching this.gamepadIndex
    const pad = (() => {
      if (typeof navigator === "undefined" || !navigator.getGamepads) return null;
      const pads = navigator.getGamepads();
      let seen = 0;
      for (const p of pads) {
        if (p && p.connected) {
          if (seen === this.gamepadIndex) return p;
          seen++;
        }
      }
      return null;
    })();
    if (pad) {
      const dz = DRONE_DEADZONE;
      const applyDz = (v: number): number => {
        if (Math.abs(v) < dz) return 0;
        return (v - Math.sign(v) * dz) / (1 - dz);
      };

      // Left stick: movement (axes 0=X, 1=Y; Y is inverted → forward)
      const lx = applyDz(pad.axes[0] ?? 0);
      const ly = applyDz(pad.axes[1] ?? 0);
      if (lx !== 0) moveX = -lx;
      if (ly !== 0) moveZ = -ly;  // stick up = forward

      // Right stick: camera look (axes 2=X, 3=Y; frame-scaled)
      const rx = applyDz(pad.axes[2] ?? 0);
      const ry = applyDz(pad.axes[3] ?? 0);
      if (rx !== 0) lookX = -rx * DRONE_CONTROLLER_LOOK_SPEED * dt;
      if (ry !== 0) lookY = ry * DRONE_CONTROLLER_LOOK_SPEED * dt;

      // LT (button 6) / RT (button 7) — analog triggers for descend/ascend
      const lt = pad.buttons[6]?.value ?? 0;
      const rt = pad.buttons[7]?.value ?? 0;
      const triggerY = rt - lt;
      if (Math.abs(triggerY) > DRONE_DEADZONE) moveY = triggerY;

      // Digital buttons — rising edge
      const btnA  = (pad.buttons[0]?.pressed ?? false);
      const btnB  = (pad.buttons[1]?.pressed ?? false);
      const btnLB = (pad.buttons[4]?.pressed ?? false);
      const btnRB = (pad.buttons[5]?.pressed ?? false);
      const prev = this.prevDroneButtons;

      if (btnA  && !prev[0])  followToggle  = true;
      if (btnB  && !prev[1])  releaseFollow = true;
      if (btnRB && !prev[5])  nextTarget    = true;
      if (btnLB && !prev[4])  previousTarget = true;

      // Right stick button (button 10) — fast mode
      if (pad.buttons[10]?.pressed) fast = true;

      this.prevDroneButtons = [
        btnA,
        btnB,
        pad.buttons[2]?.pressed ?? false,
        pad.buttons[3]?.pressed ?? false,
        btnLB,
        btnRB,
      ];
    }

    return { moveX, moveY, moveZ, lookX, lookY, fast, followToggle, releaseFollow, nextTarget, previousTarget };
  }

  /** Read camera-specific controls — completely separate from game InputState. */
  readCamera(): CameraInput {
    const typing = document.activeElement?.tagName === "INPUT";
    const lookBehindKey = getBindings().keyboard.LOOK_BEHIND ?? "KeyC";
    const keyLookBehind = !typing && this.keys.has(lookBehindKey);

    if (this.keyboardOnly) {
      return { lookBehind: keyLookBehind, freeLookX: 0, freeLookY: 0 };
    }

    const cam = this.gamepad.pollCamera();
    if (cam.cameraModePressed) this.fireCameraToggle();
    return {
      lookBehind: (this.gamepadOnly ? false : keyLookBehind) || cam.lookBehind,
      freeLookX: cam.rx,
      freeLookY: cam.ry,
    };
  }

  read(): InputState {
    if (this.keyboardOnly) return this.readKeyboard();

    const padSnap = this.gamepad.poll();

    if (padSnap.startPressed) {
      for (const fn of this.menuListeners) fn();
    }

    if (this.gamepadOnly) {
      if (padSnap.active) this.setMode("gamepad");
      return padSnap.state;
    }

    const touchState = this.touch.read();
    const touchActive = this.touch.isActive();

    const keyboardActive = this.anyKeyboardActive();
    if (padSnap.active)       this.setMode("gamepad");
    else if (touchActive)     this.setMode("touch");
    else if (keyboardActive)  this.setMode("keyboard");

    const base: InputState = (this.mode === "gamepad" && padSnap.active)
      ? padSnap.state
      : this.readKeyboard();

    return composeInputState(base, touchState, touchActive);
  }

  private readKeyboard(): InputState {
    if (document.activeElement?.tagName === "INPUT") return { ...EMPTY_INPUT };
    return keyboardInputFromBindings(this.keys, this.mouseFire, getBindings());
  }

  private anyKeyboardActive(): boolean {
    if (this.mouseFire) return true;
    const kb = getBindings().keyboard;
    const boundKeys = new Set(Object.values(kb));
    for (const code of this.keys) {
      if (boundKeys.has(code)) return true;
    }
    return false;
  }

  private setMode(m: InputDeviceMode): void {
    if (this.mode === m) return;
    this.mode = m;
    for (const fn of this.modeListeners) fn(m);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys.add(e.code);
    if (e.code === "Escape") {
      for (const fn of this.menuListeners) fn();
    }
    if (e.code === "Tab") {
      e.preventDefault();
      for (const fn of this.spectateNextListeners) fn();
    }
    if (e.code === (getBindings().keyboard.CAMERA_TOGGLE ?? "KeyV")) {
      for (const fn of this.cameraModeListeners) fn();
    }
    if (e.code === "KeyM") {
      for (const fn of this.mirrorCycleListeners) fn();
    }
    // LOOK_BEHIND is hold-to-look-behind — read each frame via readCamera(), no event needed
    // Prevent browser scroll for common drive keys when not typing in an input
    if (NON_REPEAT_PREVENT.has(e.code) && document.activeElement?.tagName !== "INPUT") {
      e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => { this.keys.delete(e.code); };

  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseFire = true;
    if (e.button === 2) this.mouseRightHeld = true;
  };
  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseFire = false;
    if (e.button === 2) this.mouseRightHeld = false;
  };
  private onMouseMove = (e: MouseEvent): void => {
    this.mouseDeltaX += e.movementX;
    this.mouseDeltaY += e.movementY;
  };

  private onGamepadConnected = (e: GamepadEvent): void => {
    this.gamepadConnected = true;
    for (const fn of this.connectionListeners) fn(true, e.gamepad.id);
  };
  private onGamepadDisconnected = (e: GamepadEvent): void => {
    this.gamepadConnected = false;
    for (const fn of this.connectionListeners) fn(false, e.gamepad.id);
    this.setMode("keyboard");
  };

  isGamepadConnected(): boolean { return this.gamepadConnected; }
}

/**
 * Pure function: derive InputState from a key set + bindings.
 * Exported so tests can call it without a DOM.
 */
export function keyboardInputFromBindings(
  keys: Set<string>,
  mouseFire: boolean,
  bindings: Bindings,
): InputState {
  const kb = bindings.keyboard;
  const isHeld = (binding: string): boolean => {
    if (!binding) return false;
    if (binding === "MouseLeft") return mouseFire;
    return keys.has(binding);
  };

  const throttle  = isHeld(kb.MOVE_FORWARD)  ? 1 : 0;
  const brake     = isHeld(kb.MOVE_BACKWARD)  ? 1 : 0;
  const steer     = (isHeld(kb.TURN_LEFT) ? 1 : 0) - (isHeld(kb.TURN_RIGHT) ? 1 : 0);
  const handbrake = isHeld(kb.HANDBRAKE);
  const fire      = isHeld(kb.FIRE);
  const fire2     = isHeld(kb.FIRE2);
  const boost     = isHeld(kb.BOOST);
  const dropWeapon = isHeld(kb.DROP_WEAPON);

  return { ...EMPTY_INPUT, throttle, brake, steer, handbrake, fire, fire2, boost, dropWeapon };
}

// Prevents browser scroll/button activation for these keys while not in an input field.
// Listed statically for Space/Arrows which are default bindings for common actions.
// Known limitation: if FIRE is rebound off Space, Space will scroll the page again.
const NON_REPEAT_PREVENT = new Set(["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);
